import React from "react";
import { Modal } from "@mui/material";
import { XIcon } from "@heroicons/react/solid";

// Regex to identify and ignore ID fields (e.g., id, statutory_id, user_id, etc.)
const isIdField = (key) => /(?:^|_)id(?:$|_)/i.test(key);

// Formatter to remove underscores from field names
const formatHeaderTitle = (key) => {
  if (!key) return "";
  return key.replace(/_/g, " ");
};

// Formatter for category names (e.g. epf -> EPF, employer_epf -> EMPLOYER EPF)
const formatCategoryTitle = (key) => {
  if (!key) return "";
  return key.replace(/_/g, " ").toUpperCase();
};

// Clean cell value formatter
const formatCellValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  if (typeof value === "number") {
    return Number.isInteger(value) ? value : parseFloat(value.toFixed(2));
  }
  return String(value);
};

// Extracts all unique non-ID fields in order from the active categories
const extractFields = (categories) => {
  const fieldSet = new Set();
  for (const [, rows] of categories) {
    if (Array.isArray(rows)) {
      for (const row of rows) {
        if (row && typeof row === "object") {
          Object.keys(row).forEach((k) => {
            if (!isIdField(k)) {
              fieldSet.add(k);
            }
          });
        }
      }
    }
  }
  return Array.from(fieldSet);
};

const StatutoryBreakupModal = ({
  isOpen,
  onClose,
  statutoryBreakup,
  employeeName,
  monthYear,
}) => {
  // Only keep categories that have at least one record (empty arrays are excluded)
  const activeCategories =
    statutoryBreakup && typeof statutoryBreakup === "object"
      ? Object.entries(statutoryBreakup).filter(
          ([, rows]) => Array.isArray(rows) && rows.length > 0
        )
      : [];

  const fields = extractFields(activeCategories);

  // Show subheaders only if any category has multiple records
  const hasMultipleRecordsPerCategory = activeCategories.some(
    ([, rows]) => rows.length > 1
  );

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="statutory-breakup-modal-title"
      aria-describedby="statutory-breakup-modal-description"
    >
      <div className="w-[92%] md:w-[85%] lg:w-[75%] xl:w-[68%] 2xl:w-[60%] max-h-[88vh] bg-white rounded-lg shadow-2xl absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col focus:outline-none overflow-hidden">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div>
            <h3
              id="statutory-breakup-modal-title"
              className="text-lg font-bold text-gray-800"
            >
              Statutory Breakup Details
            </h3>
            {(employeeName || monthYear) && (
              <p className="text-xs text-gray-500 mt-1">
                {employeeName && (
                  <span>
                    Employee: <strong className="text-gray-700">{employeeName}</strong>
                  </span>
                )}
                {employeeName && monthYear && <span> &bull; </span>}
                {monthYear && (
                  <span>
                    Period: <strong className="text-gray-700">{monthYear}</strong>
                  </span>
                )}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-gray-200 transition-colors"
            title="Close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body - Dynamic Transposed Matrix Table */}
        <div className="p-6 overflow-y-auto flex-1">
          {activeCategories.length === 0 || fields.length === 0 ? (
            <div className="py-12 text-center bg-gray-50 rounded-lg border border-dashed border-gray-300">
              <p className="text-gray-500 font-medium text-sm">
                No statutory breakup records available for this payout period.
              </p>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full divide-y divide-gray-200 text-left table-auto">
                  <thead>
                    {/* Level 1: Category Names Header */}
                    <tr className="bg-[color:var(--color1)] text-white divide-x divide-white/20">
                      <th
                        rowSpan={hasMultipleRecordsPerCategory ? 2 : 1}
                        className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-left bg-[color:var(--color1)] border-r border-white/20 min-w-[180px] w-1/4 align-middle"
                      >
                        Field
                      </th>
                      {activeCategories.map(([categoryKey, rows]) => (
                        <th
                          key={categoryKey}
                          colSpan={rows.length}
                          className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-center bg-[color:var(--color1)]"
                        >
                          {formatCategoryTitle(categoryKey)}
                        </th>
                      ))}
                    </tr>

                    {/* Level 2: Subheaders (Period 1, Period 2, ...) purely by array index */}
                    {hasMultipleRecordsPerCategory && (
                      <tr className="bg-gray-100 text-gray-700 divide-x divide-gray-200 border-b border-gray-200">
                        {activeCategories.map(([categoryKey, rows]) =>
                          rows.map((_, idx) => (
                            <th
                              key={`${categoryKey}-item-${idx}`}
                              className="px-3 py-2 text-[11px] font-semibold text-center whitespace-nowrap bg-gray-100 text-gray-700"
                            >
                              {rows.length > 1 ? `Period ${idx + 1}` : "Standard"}
                            </th>
                          ))
                        )}
                      </tr>
                    )}
                  </thead>

                  {/* Transposed Body: Each row is a dynamic Statutory Field */}
                  <tbody className="bg-white divide-y divide-gray-200">
                    {fields.map((fieldKey) => (
                      <tr
                        key={fieldKey}
                        className="hover:bg-blue-50/40 transition-colors"
                      >
                        {/* Field Label (Left Column) */}
                        <td className="px-4 py-2.5 text-xs font-semibold text-gray-800 capitalize bg-gray-50/80 border-r border-gray-200 whitespace-nowrap">
                          {formatHeaderTitle(fieldKey)}
                        </td>

                        {/* Dynamic Field Values for each Category and Record */}
                        {activeCategories.map(([categoryKey, rows]) =>
                          rows.map((row, idx) => (
                            <td
                              key={`${categoryKey}-${idx}-${fieldKey}`}
                              className="px-4 py-2.5 text-xs text-gray-700 text-center border-r border-gray-200 last:border-r-0 whitespace-nowrap"
                            >
                              {formatCellValue(row?.[fieldKey])}
                            </td>
                          ))
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 text-xs font-semibold rounded bg-gray-200 hover:bg-gray-300 text-gray-800 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default StatutoryBreakupModal;
